import { AxiosError } from 'axios';
import { ApiResponse } from './types';

export interface FieldErrors {
  [fieldName: string]: string[];
}

const isAxiosError = (err: unknown): err is AxiosError => !!(err && (err as AxiosError).isAxiosError);

const buildFieldErrors = (data: unknown): FieldErrors => {
  const fieldErrors: FieldErrors = {};
  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>;
    if (obj.errors && typeof obj.errors === 'object') {
      for (const [field, messages] of Object.entries(obj.errors as Record<string, unknown>)) {
        fieldErrors[field.toLowerCase()] = Array.isArray(messages)
          ? (messages as string[])
          : [String(messages)];
      }
    }
  }
  return fieldErrors;
};

const logResponseDetails = (url: string, response: AxiosError['response'] | undefined) => {
  if (!response) {return;}
  const { status, data, headers } = response as { status?: number; data?: unknown; headers?: Record<string, unknown> };
    const { statusText } = response as { status?: number; data?: unknown; headers?: Record<string, unknown>; statusText?: string };
  console.info('========== HTTP ERROR RESPONSE ==========');
  console.info('Status:', status);
  console.info('URL:', url);
  console.info('Status Text:', statusText);
  console.info('Response Data:', JSON.stringify(data, null, 2));
  console.info('Response Headers:', JSON.stringify(headers || {}, null, 2));
  console.info('=========================================');
};

function buildApiErrorDetails(response: AxiosError['response'] | undefined, axiosMessage: string, url: string) {
  const { status, data } = (response ?? {}) as { status?: number; data?: unknown };
  const fieldErrors = buildFieldErrors(data);
  const dataObj = data && typeof data === 'object' ? (data as Record<string, unknown>) : undefined;
  const dataMessage = dataObj?.message;
  const dataError = dataObj?.error;

  const message = typeof data === 'string' && data.trim()
    ? data
    : (typeof dataError === 'string' && dataError.trim()
      ? dataError
      : (typeof dataMessage === 'string' && dataMessage.trim()
        ? dataMessage
        : (axiosMessage || 'Request failed')));

  const cshGroupNames = Array.isArray(dataObj?.cshGroupNames)
    ? dataObj?.cshGroupNames.filter((name): name is string => typeof name === 'string' && name.trim().length > 0)
    : undefined;
  const cshGroupIds = Array.isArray(dataObj?.cshGroupIds)
    ? dataObj?.cshGroupIds.map((id) => Number(id)).filter((id) => !Number.isNaN(id) && id > 0)
    : undefined;

  return {
    status,
    message,
    url,
    fieldErrors,
    errors: (data && (data as Record<string, unknown>).errors) || (message ? [message] : []),
    cshGroupNames,
    cshGroupIds,
  };
}

function handleRequestError<T>(url: string): ApiResponse<T> {
  return {
    success: false,
    data: null,
    error: { message: 'Network error - please check your connection', code: 'NETWORK_ERROR', url }
  };
}

function handleUnknownError<T>(message: string | undefined, url: string): ApiResponse<T> {
  return {
    success: false,
    data: null,
    error: { message: message || 'Unknown error', url }
  };
}

export function mapAxiosError<T>(error: unknown, url: string): ApiResponse<T> {
  if (!isAxiosError(error)) {
    const msg = (error && typeof error === 'object' && 'message' in (error as Record<string, unknown>))
      ? String((error as Record<string, unknown>).message)
      : 'Unexpected error';

    return {
      success: false,
      data: null,
      error: { message: msg, url }
    };
  }

  const axiosErr = error as AxiosError;

  if (axiosErr.response) {
    try {
      if (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.DEV) {
        logResponseDetails(url, axiosErr.response);
      }
    } catch (e) {
      console.info('Failed to log response details:', e);
    }

    return {
      success: false,
      data: null,
      error: buildApiErrorDetails(axiosErr.response, axiosErr.message, url)
    };
  }

  if (axiosErr.request) {
    return handleRequestError<T>(url);
  }

  return handleUnknownError<T>(axiosErr.message, url);
}
